import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { consultasStockApi } from '../../api/consultasStock'
import { fotoArticuloUrl } from '../../api/notasVenta'
import { colors, font, radius, sx } from '../../design/tokens'
import { fmtFechaHora } from '../../utils/fecha'
import { BotonPrimario, Cargando, ErrorBox, ErrorMsg } from './comunes'
import { VenceChip, VerificarSinStock } from './MovilConsultasStock'

// Depósito responde consultas de stock (Martin, 21 oct 2026). Al abrir una
// consulta aparecen TODAS las pendientes de depósito (la abierta primero) para
// responder de una pasada: por producto, foto, datos para reconocerlo en la
// estantería (bodega, cepa, año, EAN), cuánto piden en grande y dos botones
// — verde Disponible / rojo Sin stock. Dos puertas, la misma pantalla: con
// sesión (/m/consultas-stock/:id) o por el link de Telegram (token).
export default function MovilConsultaStock({ token }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const [datos, setDatos] = useState(null)   // { consulta, pendientes }
  const [error, setError] = useState(null)
  const [resp, setResp] = useState({})
  const [enviando, setEnviando] = useState(false)
  const [aviso, setAviso] = useState(null)

  useEffect(() => {
    const pedir = token ? consultasStockApi.verPorToken(token) : consultasStockApi.ver(id)
    pedir
      .then(({ data }) => setDatos({ consulta: data.consulta, pendientes: data.pendientes || [] }))
      .catch((e) => setError(e.response?.status === 404
        ? 'Este link ya no sirve: no queda nada por responder o pasaron 24 h.'
        : 'No se pudo cargar la consulta.'))
  }, [id, token])

  // Grupos por consulta, en el orden que manda el backend (la abierta primero).
  // De la abierta también se muestran los productos ya respondidos.
  const grupos = useMemo(() => {
    if (!datos) return []
    const m = new Map()
    for (const it of datos.pendientes) {
      if (!m.has(it.consulta_id)) {
        m.set(it.consulta_id, {
          id: it.consulta_id, vendedor: it.vendedor, nv: it.nv_numero,
          observacion: it.consulta_observacion, creada: it.consulta_created_at, items: [],
        })
      }
      m.get(it.consulta_id).items.push(it)
    }
    const c = datos.consulta
    const respondidos = c.items.filter((it) => it.resolucion === 'consultado' && it.estado !== 'pendiente')
    if (respondidos.length) {
      if (!m.has(c.id)) m.set(c.id, { id: c.id, vendedor: c.vendedor, nv: c.nv_numero, observacion: c.observacion, creada: c.created_at, items: [] })
      m.get(c.id).items.push(...respondidos)
    }
    const lista = [...m.values()]
    lista.sort((a, b) => (a.id === c.id ? -1 : b.id === c.id ? 1 : 0))
    return lista
  }, [datos])

  if (error) return <ErrorMsg>{error}</ErrorMsg>
  if (!datos) return <Cargando />

  const pendientes = datos.pendientes
  const elegidas = pendientes.filter((it) => resp[it.id]?.respuesta)
  const cambiar = (itemId, campo, valor) => setResp((r) => ({ ...r, [itemId]: { ...r[itemId], [campo]: valor } }))

  const enviar = async () => {
    setEnviando(true)
    setAviso(null)
    try {
      const respuestas = elegidas.map((it) => ({
        item_id: it.id,
        respuesta: resp[it.id].respuesta,
        cantidad: resp[it.id].respuesta === 'disponible' && resp[it.id].cantidad !== '' && resp[it.id].cantidad != null ? Number(resp[it.id].cantidad) : null,
        observacion: resp[it.id].observacion || null,
      }))
      const { data } = token
        ? await consultasStockApi.responderPorToken(token, respuestas)
        : await consultasStockApi.responder(datos.consulta.id, respuestas)
      setDatos({ consulta: data.consulta, pendientes: data.pendientes || [] })
      setResp({})
      const errores = Object.values(data.errores || {})
      setAviso(errores.length
        ? { ok: false, texto: errores.join(' ') }
        : { ok: true, texto: `${respuestas.length === 1 ? 'Respuesta enviada' : `${respuestas.length} respuestas enviadas`}. Cada vendedor ya la ve en su NV.` })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (e) {
      setAviso({ ok: false, texto: e.response?.data?.message || 'No se pudo enviar la respuesta.' })
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div style={{ fontFamily: font.sans }}>
      <div style={{ margin: '2px 0 12px' }}>
        <h1 style={{ margin: 0, fontSize: 21, color: colors.text }}>Consultas de stock</h1>
        <div style={{ fontSize: 13, color: colors.textSec, marginTop: 2 }}>
          {pendientes.length === 0
            ? 'No queda nada por responder.'
            : `${pendientes.length} ${pendientes.length === 1 ? 'producto' : 'productos'} para responder${grupos.length > 1 ? ` · ${grupos.length} consultas` : ''}`}
        </div>
      </div>

      {aviso && (aviso.ok
        ? <div style={avisoOk}>✓ {aviso.texto}</div>
        : <ErrorBox>{aviso.texto}</ErrorBox>)}

      {pendientes.length === 0 && (
        <div style={{ ...vacio }}>
          <div style={{ fontSize: 34 }}>✅</div>
          <div style={{ fontWeight: 600, fontSize: 15, color: colors.text, marginTop: 6 }}>Todo respondido</div>
          <div style={{ fontSize: 13, color: colors.textSec, marginTop: 2 }}>Cuando Ventas pida algo nuevo te llega por Telegram.</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {grupos.map((g) => (
          <section key={g.id} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={grupoCab}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: colors.text }}>{g.vendedor || 'Ventas'}</span>
                <span style={{ ...sx.mono, fontSize: 12, color: colors.textMeta }}>#{g.id}</span>
              </div>
              <div style={{ fontSize: 12, color: colors.textSec }}>
                {g.nv ? <>NV <span style={sx.mono}>{g.nv}</span> · </> : null}{fmtFechaHora(g.creada)}
              </div>
              {g.observacion && <div style={obsConsulta}>💬 {g.observacion}</div>}
            </div>
            {g.items.map((it) => (
              <TarjetaProducto key={it.id} it={it} r={resp[it.id] || {}} onCambio={(campo, v) => cambiar(it.id, campo, v)} />
            ))}
          </section>
        ))}
      </div>

      {pendientes.length > 0 && (
        <div style={{ ...barraEnviar, bottom: token ? 0 : 'calc(62px + env(safe-area-inset-bottom, 0px))' }}>
          <BotonPrimario onClick={enviar} disabled={enviando || elegidas.length === 0} style={elegidas.length === 0 ? { opacity: 0.55 } : {}}>
            {enviando ? 'Enviando…' : elegidas.length === 0 ? 'Tocá Disponible o Sin stock' : `Enviar ${elegidas.length} de ${pendientes.length}`}
          </BotonPrimario>
        </div>
      )}

      {!token && pendientes.length === 0 && (
        <button onClick={() => navigate('/m')} style={{ ...linkBtn, marginTop: 14 }}>← Volver al inicio</button>
      )}
    </div>
  )
}

function Foto({ codigo }) {
  const [falla, setFalla] = useState(false)
  const src = fotoArticuloUrl(codigo)
  return (
    <div style={fotoBox}>
      {src && !falla
        ? <img src={src} alt="" loading="lazy" onError={() => setFalla(true)} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        : <span style={{ fontSize: 30, opacity: 0.35 }}>🍷</span>}
    </div>
  )
}

function TarjetaProducto({ it, r, onCambio }) {
  const pendiente = it.estado === 'pendiente'
  const detalle = [it.bodega, it.cepa, it.anio, it.presentacion].filter(Boolean).join(' · ')
  const dep = it.stock_presea?.depositos
  const orden = dep ? (it.stock_presea.orden || Object.keys(dep)) : []

  return (
    <div style={{ ...tarjeta, ...(r.respuesta === 'disponible' ? { borderColor: colors.ok } : r.respuesta === 'sin_stock' ? { borderColor: colors.errStrong } : {}) }}>
      <div style={{ display: 'flex', gap: 12 }}>
        <Foto codigo={it.codigo_alfa} />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.25, color: colors.text }}>{it.descripcion}</div>
          {it.verificar_sin_stock && <VerificarSinStock />}
          {detalle && <div style={{ fontSize: 12.5, color: colors.textSec }}>{detalle}</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: 12, color: colors.textMeta }}>
            <span>SKU <span style={{ ...sx.mono, color: colors.textSub }}>{it.codigo_alfa}</span></span>
            {it.ean && <span>EAN <span style={{ ...sx.mono, color: colors.textSub }}>{it.ean}</span></span>}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'stretch', gap: 10 }}>
        <div style={pideBox}>
          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', color: colors.textSec }}>PIDE</span>
          <span style={{ ...sx.mono, fontSize: 28, fontWeight: 800, lineHeight: 1, color: colors.text }}>{it.cantidad ?? '—'}</span>
        </div>
        <div style={presea}>
          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', color: colors.textSec }}>PRESEA · REFERENCIA</span>
          {dep ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {orden.map((d) => (
                <span key={d} style={depChip}>
                  <span style={{ color: colors.textMeta }}>{d}</span>
                  <b style={sx.mono}>{Number(dep[d] ?? 0)}</b>
                </span>
              ))}
            </div>
          ) : <span style={{ fontSize: 13, color: colors.textMeta }}>sin dato</span>}
          {pendiente && <VenceChip vence={it.vence_at} />}
        </div>
      </div>

      {it.suscriptos_pendientes > 0 && (
        <div style={{ fontSize: 12, color: colors.textSec }}>👥 También lo espera{it.suscriptos_pendientes > 1 ? 'n' : ''} {it.suscriptos_pendientes} vendedor{it.suscriptos_pendientes > 1 ? 'es' : ''} más</div>
      )}

      {pendiente ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <button type="button" onClick={() => onCambio('respuesta', 'disponible')} style={botonRespuesta('disponible', r.respuesta)}>✓ Disponible</button>
            <button type="button" onClick={() => onCambio('respuesta', 'sin_stock')} style={botonRespuesta('sin_stock', r.respuesta)}>✕ Sin stock</button>
          </div>
          {r.respuesta === 'disponible' && (
            <input id={`cant-${it.id}`} type="number" inputMode="numeric" min="0" placeholder="¿Cuántas hay? (opcional)"
              value={r.cantidad ?? ''} onChange={(e) => onCambio('cantidad', e.target.value)} style={campo} />
          )}
          {r.respuesta && (
            <input id={`obs-${it.id}`} type="text" placeholder="Observación (opcional)" maxLength={500}
              value={r.observacion ?? ''} onChange={(e) => onCambio('observacion', e.target.value)} style={campo} />
          )}
        </>
      ) : (
        <Resultado it={it} />
      )}
    </div>
  )
}

function Resultado({ it }) {
  if (it.estado === 'respondido') {
    const sin = it.respuesta === 'sin_stock'
    return (
      <div style={{ ...resultado, background: sin ? colors.errBg : colors.okBg, color: sin ? colors.err : colors.ok }}>
        <b>{sin ? '✕ Sin stock' : `✓ Disponible${it.cantidad_disponible !== null ? ` · ${it.cantidad_disponible}` : ''}`}</b>
        <span style={{ display: 'block', fontSize: 12, opacity: 0.85 }}>
          {it.respondido_por || 'Depósito'} · {fmtFechaHora(it.respondido_at)}{it.observacion ? ` · “${it.observacion}”` : ''}
        </span>
      </div>
    )
  }
  return <div style={{ ...resultado, background: colors.bgSoft, color: colors.textSec }}>{it.estado === 'vencido' ? 'Venció sin respuesta.' : 'Cancelada por el vendedor.'}</div>
}

// Verde y rojo siempre (Martin, 21 oct 2026): suave sin elegir, sólido el
// elegido; el otro se apaga para que se vea de un vistazo qué respondió.
const COLOR_RESPUESTA = {
  disponible: { suave: colors.okBg, fuerte: colors.ok },
  sin_stock: { suave: colors.errBg, fuerte: colors.errStrong },
}
function botonRespuesta(tipo, elegida) {
  const c = COLOR_RESPUESTA[tipo]
  const on = elegida === tipo
  return {
    ...grande,
    background: on ? c.fuerte : c.suave,
    borderColor: c.fuerte,
    color: on ? colors.bg : c.fuerte,
    opacity: elegida && !on ? 0.45 : 1,
  }
}

const tarjeta = {
  background: colors.bg, border: `1.5px solid ${colors.border}`, borderRadius: 16, padding: 12,
  display: 'flex', flexDirection: 'column', gap: 12, fontFamily: font.sans,
}
const fotoBox = {
  width: 76, height: 76, flexShrink: 0, borderRadius: 12, border: `1px solid ${colors.borderSoft}`,
  background: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
}
const pideBox = {
  minWidth: 74, padding: '8px 10px', borderRadius: 12, background: colors.bgSoft, border: `1px solid ${colors.border}`,
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
}
const presea = { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'center' }
const depChip = {
  display: 'inline-flex', gap: 5, alignItems: 'baseline', padding: '3px 8px', borderRadius: radius.pill,
  background: colors.bgSoft, border: `1px solid ${colors.border}`, fontSize: 13, color: colors.text,
}
const grande = {
  height: 54, borderRadius: 14, border: '2px solid', fontSize: 16, fontWeight: 800,
  fontFamily: font.sans, cursor: 'pointer',
}
const campo = {
  width: '100%', boxSizing: 'border-box', padding: '12px 12px', border: `1px solid ${colors.border}`,
  borderRadius: 10, fontSize: 15, fontFamily: font.sans, color: colors.text, background: colors.bg,
}
const resultado = { borderRadius: 10, padding: '9px 12px', fontSize: 14 }
const grupoCab = { display: 'flex', flexDirection: 'column', gap: 3, padding: '0 2px' }
const obsConsulta = {
  marginTop: 4, padding: '8px 10px', borderRadius: radius.md, background: colors.attnBg,
  border: `1px solid ${colors.attnBorder}`, fontSize: 13.5, color: colors.text,
}
const avisoOk = { background: colors.okBg, color: colors.ok, borderRadius: 10, padding: '10px 12px', fontSize: 13.5, fontWeight: 600, marginBottom: 12 }
const vacio = { textAlign: 'center', padding: '28px 12px', background: colors.bg, borderRadius: 16, border: `1px solid ${colors.border}` }
// Pegado abajo (encima de la barra de pestañas con sesión): el pulgar llega sin scrollear.
const barraEnviar = { position: 'sticky', marginTop: 14, padding: '10px 0', background: colors.bgSoft, zIndex: 5 }
const linkBtn = { background: 'none', border: 'none', color: colors.link, fontSize: 14, fontWeight: 600, fontFamily: font.sans, cursor: 'pointer', padding: 0 }
